import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { getLocationDetails } from '../services/api';
import { locationDetailsCache, locationDetailsInFlight } from '../services/locationDetailsCache';

// Same super-user gate OutletDropdown uses for "can switch outlets" — the Scope
// step in export modals should only appear for the same population that can
// already switch outlets via the sidebar dropdown.
const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];

export interface AccessibleOutlet {
    id: string;
    name: string;
    city?: string;
    country?: string;
}

interface OutletAccess {
    outlets: AccessibleOutlet[];
    isMultiOutlet: boolean;
    loading: boolean;
}

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const normalise = (loc: any): AccessibleOutlet => ({
    id: String(loc.id ?? loc.locationId),
    name: loc.locationName ?? loc.name ?? loc.label ?? '',
    city: loc.city,
    // Interim value only — some environments populate this directly on the branch[]
    // roster entry, others omit it entirely. The authoritative value is resolved
    // per-outlet below from each outlet's OWN /details response's top-level `country`
    // (not this nested one), since that's the only field reliably present everywhere.
    country: loc.country,
});

/**
 * Accessible-outlet list for the current user, sourced from the same
 * GET /merchants/location/{id}/details call (and shared cache) OutletDropdown
 * and BranchContext already use to populate the outlet switcher — so this
 * hook dedupes with them instead of firing an extra network call.
 */
export function useOutletAccess(): OutletAccess {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const [outlets, setOutlets] = useState<AccessibleOutlet[]>([]);
    const [loading, setLoading] = useState(true);

    const role = auth?.role ?? '';
    const isSuperUser = SUPER_ROLES.includes(role);
    const locationId = (branch as any)?.id ?? (branch as any)?.locationId ?? auth?.locationId;
    const token = findToken(auth);

    useEffect(() => {
        if (!isSuperUser || !locationId || !token) {
            setOutlets([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const cacheKey = String(locationId);

        // Each outlet's own /details response's TOP-LEVEL `country` is the only
        // reliably-populated country field — `branch[].country` on the roster
        // response is inconsistent across environments (confirmed: some environments
        // omit it entirely from every branch[] entry). Reuses the shared
        // locationDetailsCache/locationDetailsInFlight DesktopLayout/OutletDropdown
        // already populate, so an outlet the user has already visited (including the
        // current one, whose details this very roster call just cached) resolves
        // from cache instead of firing a redundant network call.
        const resolveOutletCountry = async (outletId: string): Promise<string | undefined> => {
            const key = String(outletId);
            const cached = locationDetailsCache.get(key);
            if (cached) return cached?.country;
            try {
                const inFlight = locationDetailsInFlight.get(key);
                const req = inFlight ?? getLocationDetails(outletId, token)
                    .then((res: any) => {
                        const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                        if (payload) locationDetailsCache.set(key, payload);
                        return payload;
                    })
                    .finally(() => { locationDetailsInFlight.delete(key); });
                if (!inFlight) locationDetailsInFlight.set(key, req);
                const payload = await req;
                return payload?.country;
            } catch {
                return undefined;
            }
        };

        const applyPayload = async (payload: any) => {
            if (cancelled || !payload) return;
            const list = payload?.branch ?? payload?.branches ?? [];
            const base = Array.isArray(list) ? list.map(normalise) : [];
            setOutlets(base); // ids/names available immediately; country resolves below
            if (base.length === 0) return;
            const withCountry = await Promise.all(base.map(async (o) => ({
                ...o,
                country: await resolveOutletCountry(o.id),
            })));
            if (!cancelled) setOutlets(withCountry);
        };

        const cached = locationDetailsCache.get(cacheKey);
        if (cached) {
            applyPayload(cached).finally(() => { if (!cancelled) setLoading(false); });
            return () => { cancelled = true; };
        }

        const inFlight = locationDetailsInFlight.get(cacheKey);
        const req = inFlight ?? getLocationDetails(locationId, token)
            .then((res: any) => {
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                if (payload) locationDetailsCache.set(cacheKey, payload);
                return payload;
            })
            .finally(() => { locationDetailsInFlight.delete(cacheKey); });
        if (!inFlight) locationDetailsInFlight.set(cacheKey, req);

        req
            .then((payload) => applyPayload(payload))
            .catch(() => { if (!cancelled) setOutlets([]); })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [isSuperUser, locationId, token]);

    return { outlets, isMultiOutlet: isSuperUser && outlets.length > 1, loading };
}
